import axios from 'axios';
import { ElMessage } from 'element-plus'; // 若未用ElementUI，替换为你的提示组件

// ===================== 公共配置 =====================
// 创建axios实例
// 生产环境使用相对路径 '/api'，通过服务器反向代理到后端
// 开发环境使用代理 '/api'
const api = axios.create({
  baseURL: '/api',
  timeout: 10000, // 全局默认超时时间
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  },
  // 允许跨域携带 Cookie
  withCredentials: true
});

// ===================== 请求拦截器 =====================
api.interceptors.request.use(
  (config) => {
    // 抽离Cookie获取方法
    const getCookie = (name) => {
      const nameEQ = `${name}=`;
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i].trim();
        if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length));
      }
      return null;
    };

    // 登录和注册接口不需要添加 Token 请求头
    const noAuthUrls = ['/get_userlogin', '/post_userreg'];
    const isNoAuthUrl = noAuthUrls.some(url => config.url.includes(url));
    
    if (!isNoAuthUrl) {
      // 尝试从多个来源获取Token
      let token = getCookie('HTTP_USER_TOKEN');
      if (!token) {
        token = localStorage.getItem('HTTP_USER_TOKEN');
      }

      // 获取用户ID
      const userId = localStorage.getItem('userId');

      // 添加后端需要的请求头格式: User-Token 和 User-Id
      if (token) {
        config.headers['User-Token'] = token;
        config.headers['User-Id'] = userId || '';
      } else {
        console.warn('[API请求] 未找到Token,接口:', config.url);
        console.warn('[API请求] Cookie内容:', document.cookie);
        console.warn('[API请求] localStorage内容:', localStorage.getItem('HTTP_USER_TOKEN'));
      }
    }

     return config;
  },
  (error) => {
    ElMessage.error('请求参数异常，请检查');
    return Promise.reject(error);
  }
);

// ===================== 响应拦截器 =====================
api.interceptors.response.use(
  (response) => {
    const res = response.data;
    
    // 如果响应没有code字段,直接返回原始数据
    if (res.code === undefined) {
      return res;
    }
    
    // 业务错误码处理
    if (res.code !== 0) {
      console.error('[API错误] code:', res.code, 'info:', res.info);
      console.error('[API错误] 完整错误响应:', res);
      
      // 检查是否配置了静默失败(不显示错误提示)
      if (!response.config.silentError) {
        ElMessage.error(res.info || '接口请求失败');
      }
      
      return Promise.reject(new Error(res.info || '接口请求失败'));
    }
    
    // 返回实际数据（解包到 data 层）
    return res.data;
  },
  (error) => {
    console.error('[API异常]', error.config?.url, error.response?.status, error.message);
    console.error('[API异常] 完整错误对象:', error);
    console.error('[API异常] 错误响应数据:', error.response?.data);
    
    // 分类处理错误
    let errorMsg = '网络异常，请稍后重试';
    if (error.code === 'ECONNABORTED') {
      errorMsg = '请求超时，请稍后重试';
    } else if (error.response) {
      const status = error.response.status;
      // 兼容401未登录场景
      if (status === 401) {
        errorMsg = '登录状态失效，请重新登录';
        console.warn('[401未授权] 清除登录状态并跳转登录页');
        // 清除登录状态
        localStorage.removeItem('HTTP_USER_TOKEN');
        localStorage.removeItem('userId');
        localStorage.removeItem('userName');
        localStorage.removeItem('token');
        localStorage.removeItem('avatars');
        localStorage.removeItem('tel');
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('userInfo');
        // 清除Cookie
        document.cookie = 'HTTP_USER_TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        // 跳转到登录页
        if (typeof window !== 'undefined' && window.location) {
          window.location.href = '#/login';
        }
      } else {
        errorMsg = `请求失败（${status}）：${error.response.statusText}`;
      }
    }
    ElMessage.error(errorMsg);
    return Promise.reject(error);
  }
);

// ===================== 公共方法（抽离重复逻辑） =====================
/**
 * 封装GET请求，支持自定义配置
 * @param {string} url 接口地址
 * @param {Object} params 请求参数
 * @param {Object} config 自定义配置（如timeout）
 * @returns {Promise}
 */
const getRequest = (url, params = {}, config = {}) => {
  return api.get(url, { params, ...config });
};

/**
 * 封装POST请求（multipart/form-data），支持自定义配置
 * @param {string} url 接口地址
 * @param {Object} data 请求体
 * @param {Object} config 自定义配置
 * @returns {Promise}
 */
const postFormRequest = (url, data = {}, config = {}) => {
  return api.post(url, data, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    ...config
  });
};

// ===================== 接口分类定义（规范命名+统一传参） =====================
// 1. 用户登录相关
export const userLoginApi = {
  // 登录接口：支持自定义配置
  userLogin: (params, config = {}) => getRequest('/get_userlogin', params, config),
  // 用户注册
  userRegister: (data, config = {}) => postFormRequest('/post_userreg', data, config),
  // 用户活跃度记录
  userActive: (data, config = {}) => postFormRequest('/put_useractive', data, config),
  // 用户试用权益激活
  userOrderTrial: (data, config = {}) => postFormRequest('/post_userorder_trial', data, config),
  // 记录用户访问页面
  insertViewRecord: (data, config = {}) => postFormRequest('/insert_user_viewrecords', data, config)
};

// 2. 右侧资讯相关
export const newMsgApi = {
  // 资讯流接口：支持自定义配置（解决你当前的超时问题）
  getNews: (params, config = {}) => getRequest('/get_newsflashs_flow', params, config),
  // 全局搜索内容
  getGlobalContent: (params, config = {}) => getRequest('/get_globalcontent/', params, config)
};

// 3. 聊天相关
export const chatApi = {
  // 获取聊天用户
  getChatUsers: (params, config = {}) => getRequest('/chat_users', params, config),
  // 获取聊天内容
  getChatMessages: (params, config = {}) => getRequest('/get_chat_msgs', params, config),
  // 发送聊天内容
  sendChatMessages: (data, config = {}) => postFormRequest('/send_chat_msgs', data, config)
};

// 4. 公司信息相关
export const companyApi = {
  // 获取公司基本信息
  getCompanyBase: (params, config = {}) => getRequest('/get_company_base_info', params, config),
  // 获取公司优势信息
  getCompAdvantage: (params, config = {}) => getRequest('/get_company_advantage', params, config),
  // 添加公司/项目
  addCompany: (data, config = {}) => postFormRequest('/insert_dict_company', data, config),
  //公司财务数据
  getCompanyFinancial: (params, config = {}) => getRequest('/get_financeinfos', params, config),
  // 收藏公司/组合
  insertFavobj: (data, config = {}) => postFormRequest('/insert_favobj', data, config),
  // 取消收藏公司/组合
  deleteFavobj: (data, config = {}) => postFormRequest('/delete_favobj', data, config)
};

// 5. 新三板相关
export const newThirdBoardApi = {
  // 获取做市商数据
  getMarketMaker: (params, config = {}) => getRequest('/get_market_maker', params, config),
  // 获取做市商推荐数据
  getMarketMakerRecom: (params, config = {}) => getRequest('/get_maker_recom', params, config),
  // 获取做市商做市数据
  getMarketMakerMake: (params, config = {}) => getRequest('/get_maker_make', params, config),
  // 获取做市商变动日历数据
  getMarketMakerCalendar: (params, config = {}) => getRequest('/get_maker_calendar', params, config),
  // 获取公司的做市商列表
  getBrokerList: (params, config = {}) => getRequest('/get_broker_list', params, config),
  // 获取企业行情数据（从日历跳转）
  getCompanyQuotes: (params, config = {}) => getRequest('/get_company_quotes', params, config),
  // 获取公司做市日历数据
  getCompanyCalendar: (params, config = {}) => getRequest('/get_company_calendar', params, config),
  // 获取新增挂牌列表
  getNewlyList: (params, config = {}) => getRequest('/get_new_third_newly_list', params, config),
  // 获取新增挂牌详情
  getNewlyDetail: (params, config = {}) => getRequest('/get_company_advantage', params, config),
  // 获取大宗交易数据（通用）
  getBlockTrades: (params, config = {}) => getRequest('/get_block_trades', params, config),
  // 获取大宗交易后行情数据
  getBlockTradehq: (params, config = {}) => getRequest('/get_block_tradehq', params, config),
  // 获取大宗交易AI预测数据
  getBlockTradePredict: (params, config = {}) => getRequest('/get_block_trade_predict', params, config),
  // 获取大宗交易行为回测数据
  getBlockTradePredict2: (params, config = {}) => getRequest('/get_block_trade_predict2', params, config),
  // 获取证券行情数据（通用）
  getMarketCondition: (params, config = {}) => getRequest('/get_market_condition', params, config),
  // 获取调研报告
  getReschReport: (params, config = {}) => getRequest('/get_reschreports', params, config),
  // 获取壳交易数据
  getShellTransactions: (params, config = {}) => getRequest('/get_shell_transactions', params, config),
  // 获取投资机构信息
  getInvestments: (params, config = {}) => getRequest('/get_investments', params, config)
};

// 6. 北交所相关
export const beijingStockApi = {
  // 龙虎榜
  getTradeMonitor: (params, config = {}) => getRequest('/get_trade_monitor', params, config),
  // 大宗交易（复用新三板接口）
  getBlockTrades: (params, config = {}) => newThirdBoardApi.getBlockTrades(params, config),
  // 证券行情（复用新三板接口）
  getMarketCondition: (params, config = {}) => newThirdBoardApi.getMarketCondition(params, config),
  //并购重组
  getMergerAcquisition: (params, config = {}) => getRequest('/get_merger_business', params, config),
  // 龙虎榜最近交易数据（柱状图）
  getLastTrade: (params, config = {}) => getRequest('/get_last_trade', params, config),
  // 获取北交所业务首页展示
  getBeijingStockHome: (params, config = {}) => getRequest('/get_bjs_service', params, config),
};

// 7. 资本事件相关
export const capitalEventApi = {
  // 获取资本事件（融资/IPO/定向新增）
  getCapitalInfos: (params, config = {}) => getRequest('/get_capitalinfos', params, config)
};

// 8. 股票池相关
export const stockPoolApi = {
  // 获取股票池
  getStockGroups: (params, config = {}) => getRequest('/get_groups', params, config),
  // 获取核心池标签
  getTagGroups: (params, config = {}) => getRequest('/get_taggroups', params, config),
  // 添加核心池
  addCoreGroup: (data, config = {}) => postFormRequest('/add_group', data, config),
  // 添加股票到核心池
  addComp2Group: (data, config = {}) => postFormRequest('/add_comp2group', data, config),
  // 获取股票池/标签内股票
  getStocksInGroup: (params, config = {}) => getRequest('/get_groupcomps', params, config),

  getBJtop50: (params, config = {}) => getRequest('/get_zimu_top50', params, config),
  // 获取股票池/标签内股票
  getTop50Index: (params, config = {}) => getRequest('/get_top50index', params, config),
  
  getStockPool: (params, config = {}) => getRequest('/get_poollist', params, config),
};

// 10. 一级市场项目库相关
export const primaryMarketApi = {
  // 获取项目库
  getProjectDirectory: (params, config = {}) => getRequest('/get_project_directory', params, config),
  // 获取项目库２（语义优化）
  getProjectDirectoryV2: (params, config = {}) => getRequest('/get_project_directory2', params, config),
  // 添加业务
  addBusiness: (data, config = {}) => postFormRequest('/add_business', data, config),
  // 立项
  addProjectDirectory: (data, config = {}) => postFormRequest('/add_project_directory', data, config),
  // 更新项目信息
  editProjectDirectory: (data, config = {}) => postFormRequest('/update_project_directory', data, config),
  // 添加项目历史
  addProjectDirectoryHis: (data, config = {}) => postFormRequest('/insert_project_directory_his', data, config),
  // 获取业务列表（委托定增/委托卖买壳/立项/委托调查）
  getBusiness: (params, config = {}) => getRequest('/get_business', params, config)
};

// 11. IPO相关
export const ipoApi = {
  // 获取上会委员列表
  getCommitees: (params, config = {}) => getRequest('/get_commitees', params, config),
  // 获取上会会议列表
  getReviewMeetings: (params, config = {}) => getRequest('/get_reviewmeetings', params, config),
  // 获取IPO流程
  getIpoProcess: (params, config = {}) => getRequest('/get_ipostate_bycomp', params, config),
  // 获取IPO阶段
  getIpoStage: (params, config = {}) => getRequest('/get_ipo_stage', params, config),
  // 获取IPO问询问题
  getIpoInquirys: (params, config = {}) => getRequest('/get_inquirys', params, config),
  // 获取IPO问询公司列表
  getInquirysComps: (params, config = {}) => getRequest('/get_inquiryscomps', params, config),
  // 获取IPO终止原因
  getIpoTerminal: (params, config = {}) => getRequest('/get_terminas', params, config),
  //获取IPO服务
  getIpoService: (params, config = {}) => getRequest('/get_ipo_service', params, config),
  // 获取委员审核企业列表
  getCommitterAprcomp: (params, config = {}) => getRequest('/get_committer_aprcomp', params, config),
};

// 12. 用户账户相关
export const userAccountApi = {
  // 获取用户完整信息
  getUserInfo: (params, config = {}) => getRequest('/get_userinfo', params, config),
  // 更新用户信息
  updateUserInfo: (data, config = {}) => postFormRequest('/put_userinfo', data, config),
  // 获取用户活跃度数据
  getUserActivity: (params, config = {}) => getRequest('/get_user_activity', params, config),
  // 获取我的收藏
  getMyFavorite: (params, config = {}) => getRequest('/get_myfavorite', params, config),
  // 获取用户访问记录
  getUserViewrecords: (params, config = {}) => getRequest('/get_user_viewrecords', params, config),
  // 退出登录
  logout: (data, config = {}) => postFormRequest('/put_userlogout', data, config)
};

export default api;
